import type { ReactNode } from 'react'
import { BAY_PIN_LABEL } from '@butter/library/simulatorCompanion'
import { MonitorIcon } from '#icons'
import { CodeDisplay } from '../CodeDisplay'
import { DoorAccessPanel } from '../DoorAccessPanel'
import { WorksFromNote } from '../WorksFromNote'
import { BAY_PIN_PROMPT, bayPinTitle } from '../copy'
import type { AccessBayPin } from '../types'

export interface BayPinPanelProps {
  bayPin: AccessBayPin
  /** `h2` under the mobile app's own `h1`; see {@link DoorAccessPanel}. */
  titleAs?: 'h1' | 'h2' | 'h3'
  /** Back to the door, where there is one. */
  action?: ReactNode
  /** Announce the panel on arrival — after an unlock moves the page on by itself. */
  announce?: boolean
  /** What just happened at the door ("Front Door unlocked"), said above the PIN. */
  status?: string
}

/**
 * The Bay PIN step of an access page (simulator-companion D77, the "Step 2 Bay PIN" frame):
 * the bay computer, "{bay name} PIN", the code, and when the bay starts accepting it. The same
 * panel on the app's access page and the texted link's page.
 *
 * The code shows before it works: the text arrives first, and the line says when.
 *
 * @example
 * ```tsx
 * <BayPinPanel bayPin={data.bayPin} titleAs="h2" />
 * ```
 */
export function BayPinPanel({ bayPin, titleAs, action, announce, status }: BayPinPanelProps) {
  return (
    <DoorAccessPanel
      icon={<MonitorIcon className="w-14 h-14 text-white" />}
      title={bayPinTitle(bayPin.bayName)}
      titleAs={titleAs}
      subtitle={BAY_PIN_PROMPT}
      announce={announce}
      status={status}
      action={action}
      footer={<WorksFromNote time={bayPin.opensAtLabel} />}
      data-testid="bay-pin-panel"
    >
      <CodeDisplay code={bayPin.code} available label={BAY_PIN_LABEL} />
    </DoorAccessPanel>
  )
}
